import { useRef, useEffect, useState, useCallback } from "react";
import "./TorpedoCalculator.scss";
import Nav from "../../Nav/Nav";

// ─── Types ────────────────────────────────────────────────────────────────────

interface Vec2 {
  x: number;
  y: number;
}

interface Target extends Vec2 {
  vx: number;
  vy: number;
  heading: number;
  zigTimer: number;
  zigDir: number;
}

interface Torpedo extends Vec2 {
  vx: number;
  vy: number;
  type: "straight" | "acoustic";
  trail: Vec2[];
  alive: boolean;
  age: number;
}

interface Explosion extends Vec2 {
  r: number;
  alpha: number;
}

interface SimParams {
  tSpeed: number;
  sonar: number;
  tgtSpeed: number;
  zigzag: number;
  zigAngle: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SCALE = 1;
const CANVAS_H = 300;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function knToPixPerSec(kn: number): number {
  return kn * 0.08;
}

function dist(a: Vec2, b: Vec2): number {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

function angle(from: Vec2, to: Vec2): number {
  return Math.atan2(to.y - from.y, to.x - from.x);
}

// ─── Component ───────────────────────────────────────────────────────────────

export const TorpedoCalculator = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const stateRef = useRef({
    W: 250,
    H: CANVAS_H,
    paused: false,
    torpType: "straight" as "straight" | "acoustic",
    hits: 0,
    fired: 0,
    params: {
      tSpeed: 45,
      sonar: 150,
      tgtSpeed: 15,
      zigzag: 4,
      zigAngle: 30,
    } as SimParams,
    sub: { x: 0, y: 0 } as Vec2,
    target: null as unknown as Target,
    torpedoes: [] as Torpedo[],
    explosions: [] as Explosion[],
    targetTrail: [] as Vec2[],
    lastT: null as number | null,
    animFrame: 0,
  });

  // React state

  const [hits, setHits] = useState(0);
  const [fired, setFired] = useState(0);
  const [distM, setDistM] = useState(0);
  const [bearing, setBearing] = useState(0);
  const [paused, setPaused] = useState(false);

  const [torpType, setTorpTypeState] = useState<"straight" | "acoustic">(
    "straight",
  );

  const [log, setLog] = useState<string[]>([]);

  const [params, setParams] = useState<SimParams>({
    tSpeed: 45,
    sonar: 150,
    tgtSpeed: 15,
    zigzag: 4,
    zigAngle: 30,
  });

  // ─── Log ───────────────────────────────────────────────────────────────────

  const addLog = useCallback((msg: string) => {
    const t = new Date().toLocaleTimeString("pl", {
      hour12: false,
    });

    setLog((prev) => [`[${t}] ${msg}`, ...prev.slice(0, 49)]);
  }, []);

  // ─── Target ────────────────────────────────────────────────────────────────

  const initTarget = useCallback(() => {
    const s = stateRef.current;

    s.target = {
      x: s.W * 0.45 + (Math.random() - 0.5) * 40,
      y: s.H * 0.35 + (Math.random() - 0.5) * 30,
      vx: 0,
      vy: 0,
      heading: Math.PI + 0.5 + (Math.random() - 0.5) * 0.5,
      zigTimer: 0,
      zigDir: 1,
    };

    s.targetTrail = [];
  }, []);

  // ─── Draw ──────────────────────────────────────────────────────────────────
  const draw = useCallback((ctx: CanvasRenderingContext2D) => {
    const { W, H, params, sub, target, torpedoes, explosions, targetTrail } =
      stateRef.current;

    function drawOcean() {
      const grad = ctx.createLinearGradient(0, 0, 0, H);

      grad.addColorStop(0, "#0a1a2e");
      grad.addColorStop(1, "#050d1a");

      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      ctx.strokeStyle = "rgba(255,255,255,0.03)";
      ctx.lineWidth = 0.5;

      for (let i = 0; i < W; i += 40) {
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i, H);
        ctx.stroke();
      }

      for (let j = 0; j < H; j += 40) {
        ctx.beginPath();
        ctx.moveTo(0, j);
        ctx.lineTo(W, j);
        ctx.stroke();
      }

      ctx.font = "11px monospace";
      ctx.fillStyle = "rgba(255,255,255,0.08)";

      for (let i = 0; i < W; i += 120) {
        for (let j = 0; j < H; j += 80) {
          ctx.fillText(
            `${Math.round((i / W) * 100)}N ${Math.round((j / H) * 100)}E`,
            i + 4,
            j + 12,
          );
        }
      }
    }

    function drawSonar() {
      const sonarRange = params.sonar;

      const now = Date.now() / 1000;

      const sweepAngle = (now * 1.5) % (Math.PI * 2);

      ctx.save();

      ctx.strokeStyle = "rgba(0,255,100,0.15)";
      ctx.lineWidth = 0.5;

      ctx.beginPath();
      ctx.arc(sub.x, sub.y, sonarRange, 0, Math.PI * 2);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(sub.x, sub.y);

      ctx.arc(sub.x, sub.y, sonarRange, sweepAngle, sweepAngle + 0.6);

      ctx.closePath();

      ctx.fillStyle = "rgba(0,255,100,0.04)";
      ctx.fill();

      ctx.strokeStyle = "rgba(0,255,100,0.5)";
      ctx.lineWidth = 1;

      ctx.beginPath();

      ctx.moveTo(sub.x, sub.y);

      ctx.lineTo(
        sub.x + Math.cos(sweepAngle) * sonarRange,
        sub.y + Math.sin(sweepAngle) * sonarRange,
      );

      ctx.stroke();

      ctx.restore();
    }

    function drawTargetTrail() {
      if (targetTrail.length < 2) return;

      ctx.beginPath();

      ctx.moveTo(targetTrail[0].x, targetTrail[0].y);

      for (let i = 1; i < targetTrail.length; i++) {
        ctx.lineTo(targetTrail[i].x, targetTrail[i].y);
      }

      ctx.strokeStyle = "rgba(255,160,50,0.25)";
      ctx.lineWidth = 1;

      ctx.setLineDash([3, 5]);

      ctx.stroke();

      ctx.setLineDash([]);
    }

    function drawTarget() {
      ctx.save();

      ctx.translate(target.x, target.y);

      const d = dist(sub, target);

      if (d < params.sonar) {
        ctx.beginPath();

        ctx.arc(0, 0, 22, 0, Math.PI * 2);

        const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 200);

        ctx.strokeStyle = `rgba(255,80,80,${0.3 + 0.4 * pulse})`;

        ctx.lineWidth = 1;

        ctx.stroke();
      }

      ctx.rotate(target.heading);

      ctx.fillStyle = "#e8a838";

      ctx.beginPath();

      ctx.ellipse(0, 0, 18, 7, 0, 0, Math.PI * 2);

      ctx.fill();

      ctx.fillStyle = "#c88820";

      ctx.beginPath();

      ctx.rect(-4, -9, 10, 9);

      ctx.fill();

      ctx.fillStyle = "#f0c060";

      ctx.beginPath();

      ctx.arc(0, 0, 4, 0, Math.PI * 2);

      ctx.fill();

      ctx.restore();

      ctx.font = "bold 11px monospace";
      ctx.fillStyle = "#e8a838";

      ctx.fillText("STATEK", target.x + 20, target.y - 10);
    }

    function drawSub() {
      ctx.save();

      ctx.translate(sub.x, sub.y);

      ctx.fillStyle = "#3a8acd";

      ctx.beginPath();

      ctx.ellipse(0, 0, 20, 8, 0, 0, Math.PI * 2);

      ctx.fill();

      ctx.fillStyle = "#5aadf0";

      ctx.beginPath();

      ctx.arc(0, 0, 4, 0, Math.PI * 2);

      ctx.fill();

      ctx.fillStyle = "#7cc5ff";

      ctx.fillRect(-2, -12, 4, 12);

      ctx.restore();

      ctx.font = "bold 11px monospace";
      ctx.fillStyle = "#3a8acd";

      ctx.fillText("ORP", sub.x + 22, sub.y - 8);
    }

    function drawTorpedoes() {
      for (const t of torpedoes) {
        if (t.trail.length > 1) {
          ctx.beginPath();

          ctx.moveTo(t.trail[0].x, t.trail[0].y);

          for (let i = 1; i < t.trail.length; i++) {
            ctx.lineTo(t.trail[i].x, t.trail[i].y);
          }

          ctx.strokeStyle =
            t.type === "acoustic"
              ? "rgba(150,255,150,0.5)"
              : "rgba(255,255,100,0.5)";

          ctx.lineWidth = 1.5;

          ctx.stroke();
        }

        if (!t.alive) continue;

        ctx.save();

        ctx.translate(t.x, t.y);

        const ang = Math.atan2(t.vy, t.vx);

        ctx.rotate(ang);

        ctx.fillStyle = t.type === "acoustic" ? "#5cf08a" : "#f0f05c";

        ctx.beginPath();

        ctx.ellipse(0, 0, 9, 3, 0, 0, Math.PI * 2);

        ctx.fill();

        ctx.fillStyle = "#fff";

        ctx.beginPath();

        ctx.arc(0, 0, 2, 0, Math.PI * 2);

        ctx.fill();

        ctx.restore();

        if (t.type === "acoustic") {
          const sonarRange = params.sonar;

          const d = dist(t, target);

          if (d < sonarRange) {
            ctx.beginPath();

            ctx.moveTo(t.x, t.y);

            ctx.lineTo(target.x, target.y);

            ctx.strokeStyle = "rgba(150,255,150,0.2)";
            ctx.lineWidth = 0.5;

            ctx.setLineDash([4, 4]);

            ctx.stroke();

            ctx.setLineDash([]);
          }
        }
      }
    }

    function drawExplosions() {
      for (const e of explosions) {
        ctx.beginPath();

        ctx.arc(e.x, e.y, e.r, 0, Math.PI * 2);

        ctx.fillStyle = `rgba(255,150,30,${e.alpha * 0.4})`;

        ctx.fill();

        ctx.beginPath();

        ctx.arc(e.x, e.y, e.r * 0.5, 0, Math.PI * 2);

        ctx.fillStyle = `rgba(255,255,100,${e.alpha * 0.6})`;

        ctx.fill();
      }
    }

    function drawLegend() {
      ctx.font = "11px monospace";

      const items: [string, string][] = [
        ["#f0f05c", "— Torpeda prosta"],
        ["#5cf08a", "— Torpeda akustyczna"],
        ["rgba(0,255,100,0.5)", "— Zasięg sonaru"],
        ["rgba(255,160,50,0.5)", "— Ślad celu"],
      ];

      items.forEach(([c, lbl], i) => {
        ctx.fillStyle = c;

        ctx.fillRect(10, 10 + i * 18, 20, 10);

        ctx.fillStyle = "rgba(255,255,255,0.6)";

        ctx.fillText(lbl, 34, 20 + i * 18);
      });
    }

    ctx.clearRect(0, 0, W, H);

    drawOcean();
    drawSonar();
    drawTargetTrail();
    drawSub();
    drawTarget();
    drawTorpedoes();
    drawExplosions();
    drawLegend();
  }, []);

  // ─── Update ────────────────────────────────────────────────────────────────

  const update = useCallback(
    (dt: number) => {
      const s = stateRef.current;

      const { params: p, target } = s;

      const tgtSpd = knToPixPerSec(p.tgtSpeed);

      target.x += Math.cos(target.heading) * tgtSpd * dt;
      target.y += Math.sin(target.heading) * tgtSpd * dt;

      // bounce

      if (target.x < 20 || target.x > s.W - 20) {
        target.heading = Math.PI - target.heading;
      }

      if (target.y < 20 || target.y > s.H - 20) {
        target.heading = -target.heading;
      }

      // torpedoes

      for (const t of s.torpedoes) {
        if (!t.alive) continue;

        t.age += dt;

        t.x += t.vx * dt;
        t.y += t.vy * dt;

        // acoustic homing

        if (t.type === "acoustic") {
          const ang = angle(t, target);
          const spd = Math.sqrt(t.vx ** 2 + t.vy ** 2);

          t.vx = Math.cos(ang) * spd;
          t.vy = Math.sin(ang) * spd;
        }

        // hit

        if (dist(t, target) < 18) {
          t.alive = false;

          s.hits++;
          setHits(s.hits);

          addLog("TRAFIENIE!");

          initTarget();
        }
      }

      const d = dist(s.sub, target);
      const b = ((angle(s.sub, target) * 180) / Math.PI + 360) % 360;

      setDistM(Math.round(d));
      setBearing(Math.round(b));
    },
    [addLog, initTarget],
  );

  // ─── Animation ─────────────────────────────────────────────────────────────

  const tickRef = useRef<(ts: number) => void>(() => {});

  tickRef.current = (ts: number) => {
    const s = stateRef.current;

    if (s.paused) return;

    if (s.lastT === null) {
      s.lastT = ts;
    }

    const dt = Math.min((ts - s.lastT) / 1000, 0.05);

    s.lastT = ts;

    update(dt);

    const canvas = canvasRef.current;

    if (canvas) {
      const ctx = canvas.getContext("2d");

      if (ctx) {
        draw(ctx);
      }
    }

    s.animFrame = requestAnimationFrame(tickRef.current);
  };

  // ─── Init ──────────────────────────────────────────────────────────────────

  useEffect(() => {
    const canvas = canvasRef.current;

    if (!canvas) return;

    const s = stateRef.current;

    s.W = canvas.offsetWidth || 800;
    s.H = CANVAS_H;

    canvas.width = s.W;
    canvas.height = s.H;

    s.sub = {
      x: s.W * 0.25,
      y: s.H * 0.65,
    };

    initTarget();

    s.animFrame = requestAnimationFrame(tickRef.current);

    return () => cancelAnimationFrame(s.animFrame);
  }, [initTarget]);

  // ─── Actions ───────────────────────────────────────────────────────────────

  const fireTorpedo = useCallback(() => {
    const s = stateRef.current;

    const tSpd = knToPixPerSec(s.params.tSpeed);

    const ang =
      s.torpType === "straight"
        ? angle(s.sub, s.target)
        : angle(s.sub, s.target);

    s.torpedoes.push({
      x: s.sub.x,
      y: s.sub.y,
      vx: Math.cos(ang) * tSpd,
      vy: Math.sin(ang) * tSpd,
      type: s.torpType,
      trail: [],
      alive: true,
      age: 0,
    });

    s.fired++;

    setFired(s.fired);

    addLog(
      `Wystrzelono ${
        s.torpType === "straight" ? "torpedę prostą" : "torpedę akustyczną"
      }`,
    );
  }, [addLog]);

  const resetSim = useCallback(() => {
    const s = stateRef.current;

    s.torpedoes = [];
    s.hits = 0;
    s.fired = 0;

    setHits(0);
    setFired(0);

    initTarget();

    addLog("Reset systemu.");
  }, [addLog, initTarget]);

  const togglePause = useCallback(() => {
    const s = stateRef.current;

    s.paused = !s.paused;

    setPaused(s.paused);

    s.lastT = null;

    if (!s.paused) {
      s.animFrame = requestAnimationFrame(tickRef.current);
    }
  }, []);

  const setTorpType = useCallback((t: "straight" | "acoustic") => {
    stateRef.current.torpType = t;
    setTorpTypeState(t);
  }, []);

  const handleParamChange = useCallback(
    (key: keyof SimParams, value: number) => {
      stateRef.current.params[key] = value;

      setParams((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  // ─── JSX ───────────────────────────────────────────────────────────────────

  return (
    <div id="sim-wrap">
      <Nav />
      <div id="status-row">
        <div className="stat">
          <div className="stat-val">{hits}</div>
          <div className="stat-lbl">Trafienia</div>
        </div>

        <div className="stat">
          <div className="stat-val">{fired}</div>
          <div className="stat-lbl">Wystrzelono</div>
        </div>

        <div className="stat">
          <div className="stat-val">{distM}</div>
          <div className="stat-lbl">Odległość</div>
        </div>

        <div className="stat">
          <div className="stat-val">{bearing}°</div>
          <div className="stat-lbl">Namiar</div>
        </div>
      </div>

      <canvas ref={canvasRef} id="c" className="canvas-ref-torpedo" />

      <div id="controls">
        <div className="ctrl-card">
          <h3 className="ctrl-card-name">Typ torpedy</h3>

          <div className="torpedo-type">
            <button
              className={
                torpType === "straight" ? "ttype-btn active" : "ttype-btn"
              }
              onClick={() => setTorpType("straight")}
            >
              Prosta (Mk14)
            </button>

            <button
              className={
                torpType === "acoustic" ? "ttype-btn active" : "ttype-btn"
              }
              onClick={() => setTorpType("acoustic")}
            >
              Akustyczna (Mk48)
            </button>
          </div>

          <div className="ctrl-row">
            <label>Prędkość torpedy</label>

            <input
              type="range"
              min="20"
              max="150"
              value={params.tSpeed}
              onChange={(e) =>
                handleParamChange("tSpeed", Number(e.target.value))
              }
            />

            <span>{params.tSpeed} w</span>
          </div>

          <div className="ctrl-row">
            <label>Zasięg sonarowy</label>

            <input
              type="range"
              min="50"
              max="300"
              value={params.sonar}
              onChange={(e) =>
                handleParamChange("sonar", Number(e.target.value))
              }
            />

            <span>{params.sonar} m</span>
          </div>
        </div>

        <div className="ctrl-card">
          <h3>Cel nawodny</h3>

          <div className="ctrl-row">
            <label>Prędkość celu</label>

            <input
              type="range"
              min="5"
              max="35"
              value={params.tgtSpeed}
              onChange={(e) =>
                handleParamChange("tgtSpeed", Number(e.target.value))
              }
            />

            <span>{params.tgtSpeed} w</span>
          </div>

          <div className="ctrl-row">
            <label>Częst. zygzaka</label>

            <input
              type="range"
              min="1"
              max="10"
              value={params.zigzag}
              onChange={(e) =>
                handleParamChange("zigzag", Number(e.target.value))
              }
            />

            <span>{params.zigzag}</span>
          </div>

          <div className="ctrl-row">
            <label>Kąt zygzaka</label>

            <input
              type="range"
              min="10"
              max="60"
              value={params.zigAngle}
              onChange={(e) =>
                handleParamChange("zigAngle", Number(e.target.value))
              }
            />

            <span>{params.zigAngle}°</span>
          </div>
        </div>
      </div>

      <div id="btn-row">
        <button className="primary" onClick={fireTorpedo}>
          Odpal torpedę
        </button>

        <button onClick={resetSim}>Reset</button>

        <button className="danger" onClick={togglePause}>
          {paused ? "Wznów" : "Pauza"}
        </button>
      </div>

      <div id="log">
        {log.map((entry, index) => (
          <div key={index}>{entry}</div>
        ))}
      </div>
    </div>
  );
};

export default TorpedoCalculator;
